import type { Entry } from 'contentful'
import type {
  TypeArticleSkeleton,
  TypeArticleWithoutUnresolvableLinksResponse,
} from '~/types/contentful'

export async function fetchAccessibleArticleIds({
  $client,
  $userAccessCheck,
  $visibility,
  locale,
  searchQuery,
  selectedCategory,
  highestUserLevel = 0,
}: {
  $client: ReturnType<typeof useNuxtApp>['$client']
  $userAccessCheck: ReturnType<typeof useNuxtApp>['$userAccessCheck']
  $visibility: ReturnType<typeof useNuxtApp>['$visibility']
  locale: string
  searchQuery: string
  selectedCategory: string
  highestUserLevel?: number
}) {
  const PAGE_SIZE = 1000
  let allItems: Entry<
    TypeArticleSkeleton,
    'WITHOUT_UNRESOLVABLE_LINKS',
    string
  >[] = []
  let skip = 0
  let total = 0

  // console.log(
  //   `Fetching articles with searchQuery: "${searchQuery}", selectedCategory: "${selectedCategory}"`,
  // )

  do {
    // console.log(`Fetching articles, skip: ${skip}, limit: ${PAGE_SIZE}`)

    const response =
      await $client.withoutUnresolvableLinks.getEntries<TypeArticleSkeleton>({
        content_type: 'article',
        include: 2,
        locale,
        skip,
        limit: PAGE_SIZE,
        order: ['-fields.publishedDateTime'],
        'fields.showOnNewsPage': true,
        'fields.title[match]': searchQuery,
        select: [
          'sys.id',
          'fields.visibility',
          'fields.tags',
          'fields.accessLevel',
        ],
      })

    const items = response.items ?? []
    total = response.total ?? 0

    // console.log(`Fetched ${items.length} items, total: ${total}, skip: ${skip}`)

    allItems = allItems.concat(items)
    skip += PAGE_SIZE
  } while (allItems.length < total)

  // console.log(`Fetched ${allItems.length} items, total: ${total}`)

  const filtered = allItems.filter((article) => {
    const matchesCategory = selectedCategory
      ? article.fields?.tags?.some(
          (tag) => tag?.fields?.value === selectedCategory,
        )
      : true

    const userHasAccess = $userAccessCheck(
      article.fields?.accessLevel,
      highestUserLevel,
    )

    // this is useful for debugging specific articles, uncomment when needed
    // if (article.sys.id === '3oe3yrYSx62EZuDD2gYnvQ') {
    //   console.log(article.fields?.visibility)
    //   console.log(
    //     `Article ${article.sys.id} - matchesCategory: ${matchesCategory}, userHasAccess: ${userHasAccess}, visibility: ${$visibility(article.fields?.visibility, { debug: true })}`,
    //   )
    // }

    return (
      matchesCategory &&
      $visibility(article.fields?.visibility) &&
      userHasAccess
    )
  })

  // console.log(`Filtered articles: ${filtered.length}`)

  return filtered.map((article) => article.sys.id)
}

export function filterAccessibleArticles({
  articles,
  $userAccessCheck,
  $visibility,
  selectedCategory,
  highestUserLevel = 0,
}: {
  articles: TypeArticleWithoutUnresolvableLinksResponse[]
  $userAccessCheck: ReturnType<typeof useNuxtApp>['$userAccessCheck']
  $visibility: ReturnType<typeof useNuxtApp>['$visibility']
  selectedCategory?: string
  highestUserLevel?: number
}) {
  return articles.filter((article) => {
    const matchesCategory = selectedCategory
      ? article.fields?.tags?.some(
          (tag) => tag?.fields?.value === selectedCategory,
        )
      : true

    const userHasAccess = $userAccessCheck(
      article.fields?.accessLevel,
      highestUserLevel,
    )

    return (
      matchesCategory &&
      $visibility(article.fields?.visibility) &&
      userHasAccess
    )
  })
}

export async function fetchArticlesByIdsInBatches({
  $client,
  locale,
  ids,
  batchSize = 100,
}: {
  $client: ReturnType<typeof useNuxtApp>['$client']
  locale: string
  ids: string[]
  batchSize?: number
}) {
  if (!ids.length) return [] as TypeArticleWithoutUnresolvableLinksResponse[]

  const chunks: string[][] = []
  for (let i = 0; i < ids.length; i += batchSize)
    chunks.push(ids.slice(i, i + batchSize))

  const pages = await Promise.all(
    chunks.map((chunk) =>
      $client.withoutUnresolvableLinks.getEntries<TypeArticleSkeleton>({
        content_type: 'article',
        locale,
        include: 2,
        limit: 1000,
        'sys.id[in]': chunk,
        order: ['-fields.publishedDateTime'],
        select: [
          'sys',
          'sys.id',
          'fields.title',
          'fields.excerpt',
          'fields.path',
          'fields.tags',
          'fields.visibility',
          'fields.accessLevel',
          'fields.featuredImage',
          'fields.publishedDateTime',
          'fields.hidePublishedDateTime',
        ],
      }),
    ),
  )

  return pages
    .flatMap((p) => p.items)
    .filter(Boolean) as TypeArticleWithoutUnresolvableLinksResponse[]
}
